import { networks } from '../constants/networks';
import { getAccounts, getMobileOperatingSystem } from './utils';
import { stateEventObserver } from './EventObserver';
import EventTypes from '../constants/eventTypes';
import { INPAGE_KEY, WALLET_TYPES } from '../constants/wallet';

const isBinanceProviderApproved = async () => {
  try {
    if (!window.BinanceChain || !window.BinanceChain.enable) return true;

    if (window.BinanceChain.selectedAddress) return true;

    const account = await window.BinanceChain.request({ method: 'eth_requestAccounts', params: [] });
    return !!account;
  } catch (err) {
    return false;
  }
};

/**
 * If MetaMask privacy is on, opens MetaMask modal to whitelist it
 *
 * @return {Promise<*>}
 */
const binanceProviderApprove = async () => {
  if (window.BinanceChain) return window.BinanceChain.enable();
};

let interval: NodeJS.Timeout;

export const removeBinanceProviderListeners = () => {
  if (window.BinanceChain?.removeAllListeners) {
    window.BinanceChain.removeAllListeners();
  }
  if (interval) {
    clearInterval(interval);
    interval = null;
  }
};

const subscribeBinanceProviderEvents = async (networkId: number) => {
  const unlockedAccount = await getAccounts(window.BinanceChain);
  if (window.BinanceChain) window.BinanceChain.autoRefreshOnNetworkChange = false;
  if (window.BinanceChain?.on) {
    window.BinanceChain.on('accountsChanged', async (accounts) => {
      removeBinanceProviderListeners();
      if (accounts[0]) {
        stateEventObserver.emit(EventTypes.DISCONNECT, null);
        stateEventObserver.emit(EventTypes.CONNECT_REQUEST_COMPONENT, { networkId, connectionType: WALLET_TYPES.binance });
      } else {
        stateEventObserver.emit(EventTypes.DISCONNECT, null);
      }
    });

    window.BinanceChain.on('chainChanged', async () => {
      removeBinanceProviderListeners();
      stateEventObserver.emit(EventTypes.DISCONNECT, null);
      if (!document.hidden) {
        stateEventObserver.emit(EventTypes.CONNECT_REQUEST_COMPONENT, { networkId, connectionType: WALLET_TYPES.binance });
      }
    });

    window.BinanceChain.on('disconnect', () => {
      removeBinanceProviderListeners();
      stateEventObserver.emit(EventTypes.DISCONNECT, null);
    });
  } else {
    interval = setInterval(async () => {
      const address = await getAccounts(window.BinanceChain);
      if (address?.toLowerCase() !== unlockedAccount?.toLowerCase()) {
        removeBinanceProviderListeners();
        stateEventObserver.emit(EventTypes.DISCONNECT, null);
        return clearInterval(interval);
      }
    }, 1000);
  }
};

export const connectBinanceProvider = async (networkId: number) => {
  const approved = await isBinanceProviderApproved();
  if (!approved) {
    await binanceProviderApprove();
  }
  if (await isBinanceProviderApproved()) {
    const provider = window.BinanceChain;
    if (parseInt(provider.chainId, 16) !== networkId) {
      if (networks[networkId]) {
        const params = [networks[networkId]];
        try {
          const isMobileOS = getMobileOperatingSystem() !== 'unknown';
          if (isMobileOS) {
            localStorage.setItem(INPAGE_KEY, JSON.stringify({ networkId, connectionType: WALLET_TYPES.inPageProvider }));
          }
          await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: `0x${(networkId).toString(16)}` }] });
          subscribeBinanceProviderEvents(networkId);
          return { provider };
        } catch (switchError) {
          if (switchError.code === 4902) {
            await provider.request({ method: 'wallet_addEthereumChain', params });
            subscribeBinanceProviderEvents(networkId);
            return { provider };
          }
        }
      }
      throw new Error(`Your wallet network is not the same as in the app. Please, switch to the ${networks[networkId]?.chainName} network`);
    }

    subscribeBinanceProviderEvents(networkId);
    return { provider };
  }
  throw new Error('Binance is not approved!');
};
